import { useSelector } from "react-redux";
import { T } from "react-translator-component";
import dayjs, { Dayjs } from "dayjs";
import {
  Button,
  TextField,
  Typography
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { colors } from "../../../common/colors";
import { useAppDispatch } from "../../../redux/store";
import { setItem, setVoteOptions, setVoteSetting } from "../../redux/createOpinionReducer";
import { VoteOption } from "../../util/createPost";
import Section from "../editors/Section";
import Radios from "./Radios";
import DateSelectDialog from "../DateSelectDialog";

interface Props {
  minimum: number,
  maximum: number,
  baseDate: Dayjs,
  limitDate: Dayjs,
}

export default function VoteSetting({ minimum, maximum, baseDate, limitDate }: Props) {
  const dispatch = useAppDispatch();

  const post = useSelector((state: any) => state.createOpinion.values);
  const acceptVotes = post.acceptVotes;
  const voteSetting = post.voteSetting;
  const voteOptions = voteSetting.voteOptions;
  const isChangabledVoteSetting = post.changabled.voteSetting;
  const errors = useSelector((state: any) => state.createOpinion.errors.voteSetting);

  const initialVoteOption = () => {
    const key = crypto.randomUUID()
    return {
      optionText: "",
      isVoted: false,
      key: key,
      _destroy: 0
    }
  }

  const handleAcceptVotes = (value: string) => {
    const isAccept = value === "true"
    dispatch(setItem({key: 'acceptVotes', value: isAccept}))

    if (isAccept && voteOptions.length < minimum) {
      const newVoteOption = [
        initialVoteOption(),
        initialVoteOption(),
      ]
      dispatch(setVoteOptions({value: newVoteOption}))
    }
  };

  const handleVoteValue = (value: string, index: number) => {
    const _voteOptions: VoteOption[] = Object.assign([], JSON.parse(JSON.stringify(voteOptions)));

    const deleteVoteOptions: VoteOption[] = _voteOptions.filter((option: VoteOption) => option._destroy === 1)
    const acceptVoteOptions: VoteOption[] = _voteOptions.filter((option: VoteOption) => option._destroy === 0).map(
      (option: VoteOption, i: number) => i === index ? {...option, optionText: value} : option
    )
    const newVoteOptions = [...acceptVoteOptions, ...deleteVoteOptions]
    const newVoteOptionErrors: any[] = errors.voteOptions.map(
      (error: any, i: number) => i === index ? {...error, optionText: ""} : error
    )
    dispatch(setVoteOptions({value: newVoteOptions, error: newVoteOptionErrors}))
  };

  const handleAddVoteOption = () => {
    if (appliedVoteOptions().length >= maximum) return

    setVoteOptions([...voteOptions, initialVoteOption()])
    const newVoteOption = [
      ...voteOptions,
      initialVoteOption(),
    ]
    dispatch(setVoteOptions({value: newVoteOption}))
  }

  const removeVoteOption = (key: string) => {
    const deleteOption = voteOptions.find((option: VoteOption) => option.key === key )

    if (!deleteOption) return

    let newVoteOptions: VoteOption[] = Object.assign([], JSON.parse(JSON.stringify(voteOptions)));
    if (deleteOption?.id) {
      newVoteOptions = newVoteOptions.map((option: VoteOption) => {
        if (option.key === key) {
          option._destroy = 1
        }
        return option
      })
    } else {
      newVoteOptions = newVoteOptions.filter((option: VoteOption) => option.key !== key )
    }
    dispatch(setVoteOptions({value: newVoteOptions}))
  };

  const appliedVoteOptions = () => {
    return voteOptions.filter((option: VoteOption) => option._destroy === 0)
  }

  return (
    <Section
      title={`${T("vote_setting")}${T("required")}`}
      body={
        <>
          <Radios
            value={acceptVotes}
            options={[
              { value: true, label: T("yes"), disabled: !isChangabledVoteSetting },
              { value: false, label: T("no"), disabled: !isChangabledVoteSetting },
            ]}
            disabled={!isChangabledVoteSetting}
            onChange={(value) => handleAcceptVotes(value)}
          />
          <br />

          {acceptVotes && (
            <div>
              <Typography>{T("vote_question")}</Typography>
              <TextField
                sx={ styles.width100 }
                style={styles.voteTitle}
                placeholder={`${T("vote_question_placeholder")}`}
                multiline
                rows={2}
                helperText={errors?.voteQuestion}
                error={!!errors?.voteQuestion}
                hiddenLabel
                inputProps={{ maxLength: 500 }}
                value={voteSetting.voteQuestion}
                onChange={(e) => dispatch(setVoteSetting({key: 'voteQuestion', value: e.target.value}))}
              />

              {appliedVoteOptions().map((option: VoteOption, index: number) => (
                <div style={ styles.voteSection } key={option.key}>
                  <Typography>回答0{index + 1}</Typography>
                  <div style={styles.voteInputContainer}>
                    <TextField
                      sx={ styles.voteAnswer }
                      hiddenLabel
                      placeholder={index < 3 ? T(`vote_option_placeholder_${index}`)[0] : ""}
                      inputProps={{ maxLength: 500 }}
                      value={option.optionText}
                      onChange={(e) => handleVoteValue(e.target.value, index)}
                    />
                    {(index > 1 && voteOptions.length > minimum && !option.isVoted) ? (
                      <DeleteIcon
                        sx={styles.deleteIcon}
                        onClick={() => removeVoteOption(option.key)}
                        color={"error"}
                      />
                    ) : (
                      <div style={styles.deleteIcon} />
                    )}
                  </div>
                  {errors?.voteOptions && errors.voteOptions[index]?.optionText &&
                    <Typography sx={ styles.voteError }>{errors.voteOptions[index].optionText}</Typography>
                  }
                </div>
              ))}
              <br />
              {errors?.limitVoteOptions &&
                <Typography sx={ styles.voteError }>{errors.limitVoteOptions}</Typography>
              }

              <Button
                disabled={appliedVoteOptions().length >= maximum}
                color="secondary"
                sx={styles.submitButton}
                size={"large"}
                variant="outlined"
                onClick={() => handleAddVoteOption()}
              >
                {T("add_vote_option")}
              </Button>

              <span style={styles.dateTitle}>
                {T("vote_end_date")}
                <span style={ styles.colerGray200 }>{T("vote_end_date_description")}</span>
              </span>
              <DateSelectDialog
                inputedDate={voteSetting.expiryDate ? dayjs(voteSetting.expiryDate) : baseDate.add(20, "days")}
                minDate={baseDate}
                maxDate={limitDate}
                errorMessage={errors?.expiryDate}
                onChange={(date) => dispatch(setVoteSetting({key: 'expiryDate', value: date}))}
              />
            </div>
          )}
        </>
      }
    />
  )
}

const styles = {
  submitButton: {
    width: "80%",
    alignSelf: "center",
    margin: "0 auto",
    display: "flex",
    borderRadius: 20,
    border: "2px solid",
    marginBottom: 8,
  },

  voteInputContainer: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    flexDirection: "row" as const,
  },

  deleteIcon: {
    height: 30,
    width: 30,
  },

  dateTitle: {
    marginBottom: 10,
    fontFamily: "mplus-1c-bold",
    marginTop: 20,
  },

  voteTitle: {
    marginTop: "15px",
    background: colors.farm_white,
    width: "100%"
  },

  width100: {
    width: "100%"
  },

  voteSection: {
    marginTop: 20,
  },

  voteAnswer: {
    width: "100%",
    background: colors.farm_white
  },

  voteError: {
    fontSize: 12,
    color: colors.farm_red600
  },

  colerGray200: {
    color: colors.farm_gray200
  },
};
